import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { backendUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, X } from "lucide-react";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const viewUrl = (docId) => `${backendUrl}/api/documenti/${docId}/download?inline=1&token=${localStorage.getItem("rb_token")}`;

function Dialogo({ titolo, children, onClose, onConfirm, busy, cta }) {
  return (
    <div className="fixed inset-0 z-[70] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pagamento-dialog">
      <div className="bg-white rounded-[1.5rem] w-full max-w-lg p-4 border-t-2 border-[#C9A84C] shadow-[0_20px_40px_rgba(0,0,0,0.12)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-2xl font-semibold tracking-tight text-[#1A2B4A]">{titolo}</h3>
          <button data-testid="pagamento-dialog-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
        </div>
        {children}
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] transition-colors">Annulla</button>
          <button data-testid="pagamento-dialog-confirm" onClick={onConfirm} disabled={busy}
            className="px-5 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Genero…" : cta}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "px-3 py-2 bg-white border border-[#1A2B4A]/15 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40";
const labelCls = "text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]";

export default function PraticaPagamenti({ pratica, embedded = false }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [rpOpen, setRpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const residuo = Math.max(0, Number(pratica.importo_totale || 0) - Number(pratica.importo_pagato || 0));
  const [rp, setRp] = useState({ importo: "", causale: "", giorni_scadenza: 15, piano: [] });

  const { data, isLoading } = useQuery({
    queryKey: ["documenti-contabili", pratica.id],
    queryFn: async () => (await api.get(`/pratiche/${pratica.id}/documenti-contabili`)).data,
  });
  const richieste = data?.richieste || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["documenti-contabili", pratica.id] }); qc.invalidateQueries({ queryKey: ["fatture"] }); };

  const apriRp = () => { setRp({ importo: residuo || "", causale: "", giorni_scadenza: 15, piano: [] }); setRpOpen(true); };

  const creaRichiesta = async () => {
    if (!(Number(rp.importo) > 0)) return toast.error("Indica l'importo totale");
    setBusy(true);
    try {
      await api.post(`/pratiche/${pratica.id}/richiesta-pagamento`, {
        importo: Number(rp.importo), causale: rp.causale, giorni_scadenza: Number(rp.giorni_scadenza) || 15,
        piano: rp.piano.filter((r) => Number(r.importo) > 0),
      });
      toast.success("Richiesta di pagamento creata e salvata in Drive → Contabilità");
      setRpOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Documento non generato"); }
    finally { setBusy(false); }
  };


  const emailRichiesta = async (r) => {
    try {
      const { data: res } = await api.post(`/richieste-pagamento/${r.id}/email-draft`, {});
      toast.success(`Bozza pronta in Gmail per ${res.to} — rileggila e invia tu`);
      if (res.draft_link) window.open(res.draft_link, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Bozza non creata"); }
  };
  const delRichiesta = async (r) => {
    if (!window.confirm("Eliminare questa richiesta di pagamento? Va nel cestino anche su Drive.")) return;
    try { await api.delete(`/richieste-pagamento/${r.id}`); toast.success("Richiesta eliminata"); refresh(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const setRiga = (i, patch) => setRp((s) => ({ ...s, piano: s.piano.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const pianoTot = rp.piano.reduce((s, r) => s + Number(r.importo || 0), 0);

  return (
    <div data-testid="pratica-pagamenti" className={embedded ? "sub mt-[18px]" : "sez mt-6"}>
      <div className="subt">
        <span>Richieste di pagamento</span>
        <button data-testid="nuova-richiesta-pagamento" onClick={apriRp}
          className="text-[12px] font-semibold normal-case tracking-normal text-[#8A6E2F] hover:text-[#6B5523]">
          Crea richiesta
        </button>
      </div>

      {isLoading ? (
        <div className="riga text-[#5A5A5A]"><Loader2 className="w-4 h-4 animate-spin" /> Carico i documenti…</div>
      ) : richieste.length === 0 ? (
        <div className="riga text-[#5A5A5A]" data-testid="richieste-vuote">
          Nessuna richiesta di pagamento emessa. La ricevuta di un incasso si emette dal pulsante in cima alla sezione.
        </div>
      ) : richieste.map((r) => (
        <div key={r.id} data-testid={`richiesta-${r.id}`} className="riga flex-wrap gap-y-1">
          <span className="w-[92px] shrink-0 text-[#4A4A48]">{r.data}</span>
          <span className="flex-[999_1_180px] min-w-0">
            <span className="font-semibold">{eur(r.importo)}</span>
            <span className="text-[#4A4A48]">
              {r.data_scadenza ? ` · scadenza ${r.data_scadenza}` : ""}
              {r.piano?.length ? ` · ${r.piano.length} acconti` : ""}
              {(r.acconti_scaduti || []).length > 0 ? " · " : ""}
            </span>
            {(r.acconti_scaduti || []).length > 0 && (
              <span data-testid={`acconti-scaduti-${r.id}`} className="text-[#9A3B13] font-semibold"
                title={r.acconti_scaduti.map((x) => `${x.descrizione}: attesi ${eur(x.importo)} il ${x.data}, mancano ${eur(x.mancante)} (${x.giorni} gg)`).join(" · ")}>
                {r.acconti_scaduti.length} acconti non incassati
              </span>
            )}
          </span>
          <span className="flex items-center gap-3.5 shrink-0">
            <a data-testid={`richiesta-${r.id}-view`} href={r.documento_id ? viewUrl(r.documento_id) : r.drive_link}
              target="_blank" rel="noreferrer" className="text-[13px] font-semibold no-underline">Vedi</a>
            <button data-testid={`richiesta-${r.id}-email`} onClick={() => emailRichiesta(r)}
              className="text-[13px] font-semibold text-[#8A6E2F] hover:text-[#6B5523]">Email</button>
            {r.wa_link && (
              <a data-testid={`richiesta-${r.id}-wa`} href={r.wa_link} target="_blank" rel="noreferrer"
                className="text-[13px] font-semibold no-underline">WhatsApp</a>
            )}
            {can("elimina_fatture") && (
              <button data-testid={`richiesta-${r.id}-delete`} onClick={() => delRichiesta(r)}
                className="text-[#9b2c2c]/60 hover:text-[#9b2c2c]"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </span>
        </div>
      ))}

      {rpOpen && (
        <Dialogo titolo="Richiesta di Pagamento" cta="Genera" busy={busy} onClose={() => setRpOpen(false)} onConfirm={creaRichiesta}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Importo totale (€)</label>
              <input data-testid="rp-importo" type="number" autoFocus value={rp.importo}
                onChange={(e) => setRp({ ...rp, importo: e.target.value })} className={`${inputCls} w-full mt-1`} />
            </div>
            <div>
              <label className={labelCls}>Scadenza entro (giorni)</label>
              <input data-testid="rp-scadenza" type="number" value={rp.giorni_scadenza}
                onChange={(e) => setRp({ ...rp, giorni_scadenza: e.target.value })} className={`${inputCls} w-full mt-1`} />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelCls}>Causale</label>
            <input data-testid="rp-causale" placeholder={`Assistenza pratica ${pratica.tipo_pratica_nome || ""}`} value={rp.causale}
              onChange={(e) => setRp({ ...rp, causale: e.target.value })} className={`${inputCls} w-full mt-1`} />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls}>Piano di pagamento (acconti)</label>
              <button data-testid="rp-add-acconto"
                onClick={() => setRp({ ...rp, piano: [...rp.piano, { descrizione: `${rp.piano.length + 1}° acconto`, importo: "", data: "" }] })}
                className="inline-flex items-center gap-1 text-xs text-[#1A2B4A] hover:text-[#C9A84C]"><Plus className="w-3.5 h-3.5" /> Aggiungi acconto</button>
            </div>
            {rp.piano.length === 0 && <p className="text-[11px] text-[#4a5568]">Facoltativo: senza acconti il documento riporta il solo importo totale.</p>}
            <div className="space-y-2">
              {rp.piano.map((r, i) => (
                <div key={i} className="flex gap-2" data-testid={`rp-acconto-${i}`}>
                  <input placeholder="Descrizione (es. alla firma)" value={r.descrizione} data-testid={`rp-acconto-desc-${i}`}
                    onChange={(e) => setRiga(i, { descrizione: e.target.value })} className={`${inputCls} flex-1 min-w-0`} />
                  <input type="date" value={r.data} data-testid={`rp-acconto-data-${i}`}
                    onChange={(e) => setRiga(i, { data: e.target.value })} className={`${inputCls} w-36`} />
                  <input type="number" placeholder="€" value={r.importo} data-testid={`rp-acconto-importo-${i}`}
                    onChange={(e) => setRiga(i, { importo: e.target.value })} className={`${inputCls} w-24`} />
                  <button data-testid={`rp-acconto-remove-${i}`} onClick={() => setRp({ ...rp, piano: rp.piano.filter((_, j) => j !== i) })}
                    className="text-[#4a5568] hover:text-[#b00020]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {rp.piano.length > 0 && (
              <p data-testid="rp-piano-totale" className="text-[11px] text-[#4a5568] mt-2">
                Somma acconti {eur(pianoTot)} · residuo a saldo {eur(Math.max(0, Number(rp.importo || 0) - pianoTot))}
              </p>
            )}
          </div>
          <p className="text-[11px] text-[#4a5568] mt-4">
            Nel PDF vengono stampate le istruzioni di pagamento con il link {data?.link_pagamento}.
          </p>
        </Dialogo>
      )}

    </div>
  );
}
